/**
 * Read-only summary of internal agenda goals (GET /internal-agenda). Gated server-side; not used on public surfaces.
 * Primary free-form copy lives on `privateNotes` per InternalAgendaItemSchema — there is no separate goal headline field.
 */

import { useEffect, useState } from 'react'
import { Loader2, Lock } from 'lucide-react'
import { internalAgendaAPI } from '../../services/api'
import { decodeHtmlEntities } from '../../utils/displayText'

function publicTitle(linkedId, publicAgenda) {
  if (!linkedId || !Array.isArray(publicAgenda)) return '—'
  const found = publicAgenda.find((a) => (a.agendaItemId ?? a.id) === linkedId)
  return found?.title ? String(found.title) : linkedId
}

function formatDesiredOutcome(raw) {
  const s = String(raw ?? '').replace(/_/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—'
}

/** One-line negotiation target from persisted row (same fields as agenda builder / Zod schema). */
function formatTargetLine(row) {
  if (!row || row.targetType === 'none' || !row.targetValue) return null
  const tv = row.targetValue
  try {
    if (tv.kind === 'money') {
      const major = Number(tv.amountMinorUnits) / 100
      return `Target: ${new Intl.NumberFormat(undefined, { style: 'currency', currency: tv.currency }).format(major)}`
    }
    if (tv.kind === 'percent') {
      const pct = Number(tv.basisPoints) / 100
      return `Target: ${pct}%`
    }
    if (tv.kind === 'duration') {
      return `Target: ${tv.minutes} min`
    }
    if (tv.kind === 'scope') {
      return `Target (scope): ${tv.scopeToken}`
    }
  } catch {
    return null
  }
  return null
}

export default function InternalAgendaSummaryPanel({ meetingId, publicAgendaItems }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    internalAgendaAPI
      .get(meetingId)
      .then((res) => {
        const data = res?.data ?? res
        const ia = data?.internalAgenda
        if (!cancelled) setState(ia ?? null)
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Could not load internal goals')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [meetingId])

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading internal goals…
      </div>
    )
  }

  if (err) {
    return <p className="text-sm text-amber-800 bg-amber-50/80 rounded-xl p-4">{err}</p>
  }

  const items = state?.items ?? []
  if (items.length === 0) {
    return (
      <div className="text-center py-10 text-gray-500 text-sm">
        <Lock className="w-8 h-8 mx-auto mb-2 text-gray-400" />
        No internal goals saved yet. Add them in the agenda builder — they are only available to the meeting organizer.
      </div>
    )
  }

  return (
    <ul className="space-y-3">
      {items.map((row) => {
        const notes = (row.privateNotes != null && String(row.privateNotes).trim() !== ''
          ? String(row.privateNotes).trim()
          : null)
        const targetLine = formatTargetLine(row)
        return (
          <li
            key={row.internalAgendaItemId}
            className="p-4 rounded-xl border border-violet-100 bg-violet-50/40 text-left"
          >
            <p className="text-xs font-semibold text-violet-800 uppercase tracking-wide">Internal goal</p>
            {notes ? (
              <p className="text-sm text-gray-900 mt-2 whitespace-pre-wrap leading-relaxed">
                {decodeHtmlEntities(notes)}
              </p>
            ) : (
              <p className="text-sm text-gray-500 mt-2 italic">
                No private goal text saved for this row — add details in the agenda builder.
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Goal category:{' '}
              <span className="text-gray-700">{formatDesiredOutcome(row.desiredOutcome)}</span>
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Linked public topic:{' '}
              <span className="font-medium text-gray-800">
                {publicTitle(row.linkedPublicAgendaItemId, publicAgendaItems)}
              </span>
            </p>
            {targetLine ? (
              <p className="text-xs text-gray-600 mt-2">{targetLine}</p>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}
